import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { videos, videoCategories } from '@/mocks/videos';

const ITEMS_PER_PAGE = 12;

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = videoCategories.find(c => c.slug === slug);

  const categoryVideos = useMemo(() => {
    let result = slug ? videos.filter(v => v.categorySlug === slug) : videos;

    switch (sortBy) {
      case 'popular':
        result = [...result].sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result = [...result].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
        break;
    }
    return result;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(categoryVideos.length / ITEMS_PER_PAGE);
  const paginatedVideos = categoryVideos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部'} - 密桃视频`,
    description: `密桃视频${currentCategory?.name || '影视'}分类，高清全集免费在线观看不缓冲`,
    url: window.location.href,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: currentCategory?.name || '分类' },
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">
        {/* Category Header */}
        <div className="py-6 md:py-8">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
            {currentCategory?.name || '全部视频'}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            共 {categoryVideos.length} 部影片，高清全集免费在线观看不缓冲
          </p>
        </div>

        {/* Sub-category navigation */}
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 flex-wrap">
          {videoCategories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              onClick={() => handleCategoryChange(cat.slug)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                slug === cat.slug || (cat.slug === 'all' && !slug)
                  ? 'bg-accent-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`} />
              {cat.name}
            </Link>
          ))}
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-foreground-500">排序：</span>
          {[
            { key: 'latest' as const, label: '最新' },
            { key: 'popular' as const, label: '最热' },
            { key: 'rating' as const, label: '评分' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => { setSortBy(opt.key); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                sortBy === opt.key
                  ? 'bg-secondary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Video Grid */}
        {paginatedVideos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {paginatedVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-video-line text-2xl w-8 h-8 flex items-center justify-center" />
            </div>
            <p className="text-foreground-500">暂无相关内容</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="flex items-center justify-center gap-1 mt-10" aria-label="分页导航">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded-md text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  currentPage === page
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded-md text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            </button>
          </nav>
        )}
      </div>
    </>
  );
}